import validations from "constants/validations";

import { Stack } from "@mui/material";
import DialogContentText from "@mui/material/DialogContentText";
import { deleteUser, logout } from "API/calls";
import CodeField from "components/elements/CodeField";
import Modal from "components/elements/Modal";
import PasswordField from "components/elements/PasswordField";
import { useUserDataContext } from "context/UserDataContext";
import { useFormik } from "formik";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

interface Props {
  onClose: () => void;
}

const ModalDeleteAccount = ({ onClose }: Props) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { mfaEnabled } = useUserDataContext();

  const handleDeteleAccount = async ({ password, otp }: { password: string; otp: string }) => {
    try {
      await deleteUser(password, mfaEnabled ? otp : undefined);
      await logout();
      window.location.replace("/");
    } catch (error: any) {
      if (error?.response?.status === 403) {
        enqueueSnackbar(
          mfaEnabled ? t("settingsPage.deleteAccountWrongPasswordOrOtp") : t("settingsPage.deleteAccountWrongPassword"),
          { variant: "error" }
        );
      } else {
        enqueueSnackbar(t("commonError"), { variant: "error" });
      }
    }
  };

  const formik = useFormik({
    initialValues: {
      password: "",
      otp: "",
    },
    validationSchema: yup.object().shape({
      password: yup.string().required(t("fieldRequired")).matches(validations.password, t("fieldErrors.password")),
      otp: mfaEnabled
        ? yup
            .string()
            .required(t("fieldRequired"))
            .max(6, t("fieldMaxLength", { max: 6 }))
        : yup.string().notRequired(),
    }),
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: handleDeteleAccount,
  });

  return (
    <Modal
      open
      onCancel={onClose}
      title={`${t("settingsPage.deleteAccount")}?`}
      onConfirm={formik.handleSubmit}
      withCloseButton
      confirmTitle={t("confirm")}>
      <DialogContentText sx={{ mb: 2 }}>{t("settingsPage.deleteAccountConfirm")}</DialogContentText>
      <Stack spacing={2}>
        <PasswordField
          fullWidth={true}
          id={"password"}
          name={"password"}
          label={t("password")}
          value={formik.values.password}
          error={Boolean(formik.errors.password)}
          helperText={formik.errors.password}
          onChange={formik.handleChange}
          autoComplete={"current-password"}
          autoFocus
        />
        {mfaEnabled ? (
          <CodeField
            sx={{ pb: 1 }}
            fullWidth={true}
            maxLength={6}
            id={"otp"}
            name={"otp"}
            label={t("twoFactorAuth.enableGoogleAuth.label")}
            value={formik.values.otp}
            error={Boolean(formik.errors.otp)}
            helperText={formik.errors.otp ?? t("twoFactorAuth.enableGoogleAuth.helperText")}
            onChange={formik.handleChange}
          />
        ) : null}
      </Stack>
    </Modal>
  );
};

export default ModalDeleteAccount;
